import { Controller } from "stimulus";

// Waiting-room countdown -> reload into the playing state at T-0; reveal a "chat closes in"
// countdown once a premiere's video finishes; redirect to the episode when a premiere's window
// closes. Shared by both live streams and premieres now - a stream has no window-close redirect
// (mark_live!/mark_idle! drive its transitions via turbo_stream_from instead), so the view simply
// leaves feedItemUrl/endsAtMs blank for a stream and watchWindowEnd below no-ops on its own guard.
// waitingValue is the ticking-countdown gate, not the same thing as the page's own "waiting room"
// content state: a deferred premiere or a stream stuck past its own starts_at also render
// waiting-room copy, but with no live target to count down to, so the view leaves waitingValue
// false for those - see live_pages/show.html.erb's countdown_target_ms.
export default class ShowController extends Controller {
  static targets = ["countdown", "chatClose", "chatCloseBar"];
  static values = {
    startsAtMs: Number,
    endsAtMs: Number,
    serverNowMs: Number,
    playbackEndsAtMs: Number,
    feedItemUrl: String,
    waiting: Boolean,
  };

  connect() {
    // Offset a skewed client clock onto server time (minted at render).
    this.clockOffset = this.serverNowMsValue - Date.now();

    if (this.waitingValue) {
      this.renderCountdown();
      this.countdownInterval = window.setInterval(() => this.renderCountdown(), 1000);
    }

    this.watchAfterparty();
    this.watchWindowEnd();
  }

  disconnect() {
    if (this.countdownInterval) window.clearInterval(this.countdownInterval);
    if (this.windowEndInterval) window.clearInterval(this.windowEndInterval);
    if (this.afterpartyInterval) window.clearInterval(this.afterpartyInterval);
  }

  now() {
    return Date.now() + this.clockOffset;
  }

  renderCountdown() {
    const remaining = this.startsAtMsValue - this.now();
    if (remaining <= 0) {
      if (this.countdownInterval) window.clearInterval(this.countdownInterval);
      // Reload so the server renders the playing state (fresh signed URL + player).
      window.location.reload();
      return;
    }
    if (this.hasCountdownTarget) this.countdownTarget.textContent = this.format(remaining);
  }

  // Reveal the "chat closes in M:SS" line and count it down to the afterparty end - both when the page
  // loads mid-afterparty and when the video finishes while the viewer is watching (no reload).
  watchAfterparty() {
    if (!this.playbackEndsAtMsValue || !this.hasChatCloseBarTarget) return;

    const tick = () => {
      if (this.now() < this.playbackEndsAtMsValue) return;
      this.chatCloseBarTarget.classList.remove("hidden");
      if (this.hasChatCloseTarget) {
        this.chatCloseTarget.textContent = this.format(Math.max(0, this.endsAtMsValue - this.now()));
      }
    };

    tick();
    this.afterpartyInterval = window.setInterval(tick, 1000);
  }

  // Window end can be hours out; setTimeout is unreliable over long spans/sleep, so poll the offset clock.
  watchWindowEnd() {
    if (!this.feedItemUrlValue || !this.endsAtMsValue) return;

    this.windowEndInterval = window.setInterval(() => {
      if (this.now() >= this.endsAtMsValue) {
        window.clearInterval(this.windowEndInterval);
        window.location.assign(this.feedItemUrlValue);
      }
    }, 1000);
  }

  // MM:SS, widening to H:MM:SS once an hour or more remains.
  format(ms) {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    if (hours > 0) return `${hours}:${this.pad(minutes)}:${this.pad(seconds)}`;
    return `${this.pad(minutes)}:${this.pad(seconds)}`;
  }

  pad(value) {
    return String(value).padStart(2, "0");
  }
}
